/*
 * Saved filter presets for the Reminders page.
 *
 * A preset is a named snapshot of the persisted filter subset
 * (PERSISTED_FILTER_KEYS — due-date range excluded, same reason as the
 * "last used" persistence). Stored in localStorage under a versioned key.
 *
 * Shape: { presets: [{ id, name, isDefault, filters: {...} }] }
 *
 * All reads are defensive: a missing/corrupt/old payload yields [] rather than
 * throwing, and every preset's filters are sanitised down to the known keys so
 * applying an old preset after new filter keys are added is safe (unknown keys
 * dropped, missing keys left to the caller's defaults).
 */

import { PERSISTED_FILTER_KEYS } from './constants';

export const PRESETS_STORAGE_KEY = 'reminders_filter_presets_v1';

// Keys that are arrays (multi-selects); everything else in the set is a string.
const ARRAY_KEYS = ['assignee_uuids', 'involvement'];

// Reduce an arbitrary object to the known filter keys with correct types.
export function sanitizePresetFilters(filters) {
  const out = {};
  if (!filters || typeof filters !== 'object') return out;
  PERSISTED_FILTER_KEYS.forEach((k) => {
    const v = filters[k];
    if (ARRAY_KEYS.includes(k)) {
      if (Array.isArray(v)) out[k] = v.filter((x) => typeof x === 'string');
    } else if (typeof v === 'string') {
      out[k] = v;
    }
  });
  return out;
}

// Read + validate the stored preset list. Never throws.
export function readPresets() {
  try {
    const raw = localStorage.getItem(PRESETS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.presets)) return [];
    return parsed.presets
      .filter(
        (p) => p && typeof p === 'object' && typeof p.id === 'string' && typeof p.name === 'string'
      )
      .map((p) => ({
        id: p.id,
        name: p.name,
        isDefault: !!p.isDefault,
        filters: sanitizePresetFilters(p.filters)
      }));
  } catch {
    return [];
  }
}

// Persist the preset list. Best-effort — returns false on quota/serialisation
// failure rather than throwing.
export function writePresets(presets) {
  try {
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify({ presets }));
    return true;
  } catch {
    return false;
  }
}

// The single default preset, or null. Defensive against multiple isDefault=true
// rows (first wins).
export function getDefaultPreset(presets) {
  return (presets || []).find((p) => p.isDefault) || null;
}

export function makePresetId() {
  return `preset_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
